import { NUMBER_WORDS } from './number-words.js';

function parseIntWord(s: string): number | null {
  const n = /^\d+$/.test(s) ? parseInt(s, 10) : NUMBER_WORDS[s.toLowerCase()];
  return Number.isFinite(n) ? (n as number) : null;
}

const WEEKDAY_CODE: Record<string, string> = {
  monday: 'MO', mon: 'MO', lundi: 'MO', lun: 'MO',
  tuesday: 'TU', tues: 'TU', tue: 'TU', mardi: 'TU', mar: 'TU',
  wednesday: 'WE', weds: 'WE', wed: 'WE', mercredi: 'WE', merc: 'WE', mer: 'WE',
  thursday: 'TH', thurs: 'TH', thur: 'TH', thu: 'TH', jeudi: 'TH', jeud: 'TH', jeu: 'TH',
  friday: 'FR', fri: 'FR', vendredi: 'FR', vend: 'FR', ven: 'FR',
  saturday: 'SA', sat: 'SA', samedi: 'SA', sam: 'SA',
  sunday: 'SU', sun: 'SU', dimanche: 'SU', dim: 'SU',
};

export interface RruleMatch {
  rrule: string;
  raw: string;
  start: number;
  end: number;
}

const PATTERNS: Array<(text: string) => RruleMatch | null> = [
  // every DD/MM → yearly on that day (birthdays etc.)
  (text) => {
    const m = text.match(
      /\b(?:every|tous\s+les\s+ans?\s+le|chaque\s+ann[eé]e\s+le)\s+(\d{1,2})[\/\-\.](\d{1,2})\b/i
    );
    if (!m) return null;
    const day = parseInt(m[1]!, 10);
    const month = parseInt(m[2]!, 10);
    if (day < 1 || day > 31 || month < 1 || month > 12) return null;
    return {
      rrule: `FREQ=YEARLY;BYMONTH=${month};BYMONTHDAY=${day}`,
      raw: m[0],
      start: m.index!,
      end: m.index! + m[0].length,
    };
  },
  // every 1st/2nd/.../last <weekday> → monthly on that ordinal weekday
  (text) => {
    const m = text.match(
      /\b(?:every|tous\s+les|chaque)\s+(1st|2nd|3rd|4th|first|second|third|fourth|last|1er|1ère|2e|2ème|3e|3ème|4e|4ème|premier|première|deuxi[eè]me|troisi[eè]me|quatri[eè]me|dernier|derni[eè]re)\s+([a-zéèêà]+)\b/i
    );
    if (!m) return null;
    const ordinalWord = m[1]!.toLowerCase();
    const weekdayWord = m[2]!.toLowerCase();
    const code = WEEKDAY_CODE[weekdayWord];
    if (!code) return null;
    const ordinal =
      /^(?:1st|first|1er|1[eè]re|premier|premi[eè]re)$/.test(ordinalWord) ? 1 :
      /^(?:2nd|second|2e|2[eè]me|deuxi[eè]me)$/.test(ordinalWord) ? 2 :
      /^(?:3rd|third|3e|3[eè]me|troisi[eè]me)$/.test(ordinalWord) ? 3 :
      /^(?:4th|fourth|4e|4[eè]me|quatri[eè]me)$/.test(ordinalWord) ? 4 :
      /^(?:last|dernier|derni[eè]re)$/.test(ordinalWord) ? -1 : null;
    if (ordinal === null) return null;
    return {
      rrule: `FREQ=MONTHLY;BYDAY=${ordinal}${code}`,
      raw: m[0],
      start: m.index!,
      end: m.index! + m[0].length,
    };
  },
  // every <day-of-month> (e.g. "every 28") → monthly on that day
  (text) => {
    const m = text.match(
      /\b(?:every|tous\s+les|chaque|le)\s+(\d{1,2})(?:st|nd|rd|th|er|ème|eme|e)?\b(?!\s*(?:days?|jours?|weeks?|semaines?|months?|mois|years?|ans?|[\/\-\.]\d))/i
    );
    if (!m) return null;
    const day = parseInt(m[1]!, 10);
    if (day < 1 || day > 31) return null;
    return {
      rrule: `FREQ=MONTHLY;BYMONTHDAY=${day}`,
      raw: m[0],
      start: m.index!,
      end: m.index! + m[0].length,
    };
  },
  // every N days/weeks/months/years (N as digit or word)
  (text) => {
    const m = text.match(
      /\b(?:every|tous\s+les|toutes\s+les)\s+(\d+|one|two|three|four|five|six|seven|eight|nine|ten|un|une|deux|trois|quatre|cinq|sept|huit|neuf|dix)\s+(days?|jours?|weeks?|semaines?|months?|mois|years?|ans?)\b/i
    );
    if (!m) return null;
    const n = parseIntWord(m[1]!);
    if (n === null) return null;
    const unit = m[2]!.toLowerCase();
    const freq = unit.startsWith('day') || unit.startsWith('jour')
      ? 'DAILY'
      : unit.startsWith('week') || unit.startsWith('semaine')
      ? 'WEEKLY'
      : unit.startsWith('month') || unit.startsWith('mois')
      ? 'MONTHLY'
      : 'YEARLY';
    return {
      rrule: `FREQ=${freq};INTERVAL=${n}`,
      raw: m[0],
      start: m.index!,
      end: m.index! + m[0].length,
    };
  },
  // every weekday / tous les jours ouvrés
  (text) => {
    const m = text.match(
      /\b(?:every\s+weekday|tous\s+les\s+jours\s+ouvr[eé]s|weekdays?)\b/i
    );
    if (!m) return null;
    return {
      rrule: 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR',
      raw: m[0],
      start: m.index!,
      end: m.index! + m[0].length,
    };
  },
  // every day / tous les jours
  (text) => {
    const m = text.match(/\b(?:every\s+day|tous\s+les\s+jours?|daily)\b/i);
    if (!m) return null;
    return { rrule: 'FREQ=DAILY', raw: m[0], start: m.index!, end: m.index! + m[0].length };
  },
  // every month / tous les mois
  (text) => {
    const m = text.match(/\b(?:every\s+month|tous\s+les\s+mois|monthly)\b/i);
    if (!m) return null;
    return { rrule: 'FREQ=MONTHLY', raw: m[0], start: m.index!, end: m.index! + m[0].length };
  },
  // every year
  (text) => {
    const m = text.match(/\b(?:every\s+year|tous\s+les\s+ans?|yearly|annually|chaque\s+ann[eé]e)\b/i);
    if (!m) return null;
    return { rrule: 'FREQ=YEARLY', raw: m[0], start: m.index!, end: m.index! + m[0].length };
  },
  // every <weekday>(, and|et|& <weekday>)*
  (text) => {
    const prefix = text.match(/\b(?:every|tous\s+les)\s+/i);
    if (!prefix) return null;
    const start = prefix.index!;
    const sortedNames = Object.keys(WEEKDAY_CODE).sort(
      (a, b) => b.length - a.length
    );

    const days: string[] = [];
    let cursor = start + prefix[0].length;

    // Consume <weekday> (<connector> <weekday>)*
    // Connectors: ",", "and", "et", "&" (with optional whitespace around).
    for (;;) {
      const rest = text.slice(cursor);
      let matched: string | null = null;
      for (const name of sortedNames) {
        if (rest.toLowerCase().startsWith(name)) {
          const next = rest[name.length] ?? '';
          const allowPluralS =
            next.toLowerCase() === 's' && !/[A-Za-z]/.test(rest[name.length + 1] ?? '');
          if (!/[A-Za-z]/.test(next) || allowPluralS) {
            matched = name;
            break;
          }
        }
      }
      if (!matched) break;
      days.push(WEEKDAY_CODE[matched]!);
      cursor += matched.length;
      if ((text[cursor] ?? '').toLowerCase() === 's' && !/[A-Za-z]/.test(text[cursor + 1] ?? '')) {
        cursor += 1;
      }

      const conn = text.slice(cursor).match(/^\s*(?:,|and|et|&)\s*/i);
      if (!conn) break;
      cursor += conn[0].length;
    }

    if (days.length === 0) return null;
    const unique = [...new Set(days)];
    return {
      rrule: `FREQ=WEEKLY;BYDAY=${unique.join(',')}`,
      raw: text.slice(start, cursor),
      start,
      end: cursor,
    };
  },
  // every week / toutes les semaines (after more specific patterns)
  (text) => {
    const m = text.match(/\b(?:every\s+week|toutes?\s+les?\s+semaines?|weekly)\b/i);
    if (!m) return null;
    return { rrule: 'FREQ=WEEKLY', raw: m[0], start: m.index!, end: m.index! + m[0].length };
  },
];

export function findRrule(text: string): RruleMatch | null {
  for (const p of PATTERNS) {
    const m = p(text);
    if (m) return m;
  }
  return null;
}
